import { AxiosError, AxiosResponse } from 'axios';
import { getApi } from 'api';
import {
  IBulkFeatureAccessResponse,
  ICompanyFeatureAccess,
  IFeatureState,
  ParsedFeatureAccessError,
} from 'types/feature-access/IFeatureAccess';

const BASE_URL = 'FeatureAccess/Companies';

/**
 * The shared axios instance has no timeout, so a hung request would spin forever and the
 * "fails to load / times out" acceptance criteria could never be reached. Every feature access
 * call carries its own limit instead of changing the instance for the whole app.
 */
export const FEATURE_ACCESS_REQUEST_TIMEOUT_MS = 30000;
const config = { timeout: FEATURE_ACCESS_REQUEST_TIMEOUT_MS };

interface FeatureAccessErrorBody {
  error?: unknown;
  details?: Record<string, unknown>;
}

/** Branches on ErrorResponse.error, never on `message` (same approach as parseJobApplicantError). */
export const parseFeatureAccessError = (error: unknown): ParsedFeatureAccessError => {
  const response = (error as AxiosError<FeatureAccessErrorBody>)?.response;
  const body = response?.data;
  return {
    status: response?.status ?? null,
    key: typeof body?.error === 'string' ? body.error : null,
    details: body?.details ?? {},
  };
};

export const getFeatureAccessCompaniesRequest = async (): Promise<ICompanyFeatureAccess[]> => {
  const response: AxiosResponse<ICompanyFeatureAccess[]> = await getApi().get(BASE_URL, config);
  return response.data;
};

export const getCompanyFeatureAccessRequest = async (companyId: number): Promise<ICompanyFeatureAccess> => {
  const response: AxiosResponse<ICompanyFeatureAccess> = await getApi().get(`${BASE_URL}/${companyId}`, config);
  return response.data;
};

/** Sends the desired state; the backend diffs, so sending all four features is safe. */
export const updateCompanyFeatureAccessRequest = async (
  companyId: number,
  features: IFeatureState[],
): Promise<ICompanyFeatureAccess> => {
  const response: AxiosResponse<ICompanyFeatureAccess> = await getApi().put(
    `${BASE_URL}/${companyId}`,
    { features },
    config,
  );
  return response.data;
};

export const bulkUpdateFeatureAccessRequest = async (
  companyIds: number[],
  changes: IFeatureState[],
): Promise<IBulkFeatureAccessResponse> => {
  const response: AxiosResponse<IBulkFeatureAccessResponse> = await getApi().post(
    `${BASE_URL}/Bulk`,
    { companyIds, changes },
    config,
  );
  return response.data;
};
